import { useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

function czyOgraniczonyRuch(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * M-2: pokazywanie i chowanie tresci animuje WYSOKOSC, nie samo opacity. Bez tego
 * tresc pod spodem snapuje w gore, a fade tego nie leczy.
 *
 * - przejscie jest PRZERYWALNE: szybkie klikanie tam i z powrotem nie kolejkuje
 *   animacji, bo kazda zmiana startuje z biezacej wysokosci;
 * - BEZ animacji na pierwszym renderze - animujemy realna zmiane po interakcji,
 *   nie wejscie na strone;
 * - po rozwinieciu wysokosc wraca do `auto`, zeby tresc mogla urosnac (np. po
 *   doladowaniu danych) bez ponownego mierzenia;
 * - przy prefers-reduced-motion CSS wylacza przejscie i zmiana jest natychmiastowa; rozwiniecie wraca
 *   wtedy do `auto` bez czekania na transitionend, ktorego nie bedzie.
 */
export function Collapse({
  otwarty,
  children,
  id,
}: {
  otwarty: boolean;
  children: ReactNode;
  /** Do polaczenia z wyzwalaczem przez aria-controls. */
  id?: string;
}): React.JSX.Element {
  const wnetrzeRef = useRef<HTMLDivElement>(null);
  const pierwszyRef = useRef(true);
  const [wysokosc, setWysokosc] = useState<number | "auto">(otwarty ? "auto" : 0);

  useLayoutEffect(() => {
    if (pierwszyRef.current) {
      pierwszyRef.current = false;
      return;
    }
    const el = wnetrzeRef.current;
    if (!el) return;

    if (otwarty) {
      // Przy ograniczonym ruchu CSS wylacza przejscie, wiec transitionend nigdy nie przyjdzie i rozwinieta
      // tresc zostalaby na stale z wysokoscia w pikselach: zmiana szerokosci obcielaby jej dol. Wtedy
      // wracamy do `auto` od razu.
      setWysokosc(czyOgraniczonyRuch() ? "auto" : el.scrollHeight);
      return;
    }

    // Zwijanie z "auto" nie ma skad wystartowac - najpierw ustawiamy konkretna
    // wysokosc, a zero dopiero w nastepnej klatce.
    setWysokosc(el.scrollHeight);
    const klatka = requestAnimationFrame(() => setWysokosc(0));
    return () => cancelAnimationFrame(klatka);
  }, [otwarty]);

  const zamkniety = !otwarty && wysokosc === 0;

  return (
    <div
      id={id}
      className="collapse"
      style={{ height: wysokosc === "auto" ? "auto" : `${wysokosc}px` }}
      onTransitionEnd={(e) => {
        if (e.propertyName === "height" && otwarty) setWysokosc("auto");
      }}
      aria-hidden={zamkniety || undefined}
      // `inert` wyjmuje zwinieta tresc z kolejnosci Taba. Bez tego fokus wchodzi
      // w kontener o wysokosci 0 i znika z ekranu (A-4).
      {...(zamkniety ? { inert: true } : {})}
    >
      <div ref={wnetrzeRef} className="collapse__wnetrze">
        {children}
      </div>
    </div>
  );
}
